import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMaintenanceReports, usePendingMaintenanceReports } from "@/hooks/use-fleet-reports";
import { useAuth } from "@/hooks/use-auth";
import { useReportExport } from "@/hooks/use-report-export";
import { formatWorkOrderNumber } from "@/hooks/use-work-orders";
import { calculatePlanStatus } from "@/hooks/use-maintenance-plans";
import { Wrench, CheckCircle, Clock, AlertCircle, Calendar, Info, XCircle, HelpCircle, PieChart as PieChartIcon, TrendingDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { format, parseISO, isValid, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ReportHeader, KPICard, KPIGrid, MaintenanceTimeline, ProviderPerformanceComparison, MaintenanceCostAnalysis, MaintenanceRecommendations } from "@/components/fleet/reports";
import { useMemo } from "react";
import {
  calculateProviderPerformance,
  generateMaintenanceTimeline,
  calculateMonthlyTrends,
  calculateCostByType,
  generateMaintenanceRecommendations,
  identifyCriticalVehicles
} from "@/lib/maintenance-analytics";

export function MaintenanceReports() {
  const { activeCompany } = useAuth();
  const { data: maintenanceReports, isLoading, error } = useMaintenanceReports(activeCompany?.id);
  const { data: pendingReports, isLoading: isPendingLoading } = usePendingMaintenanceReports(activeCompany?.id);
  const { exportReport, isExporting } = useReportExport();

  // Memoize analytics data
  const analyticsData = useMemo(() => {
    if (!maintenanceReports || maintenanceReports.length === 0) {
      return null;
    }

    return {
      providerMetrics: calculateProviderPerformance(maintenanceReports),
      timeline: generateMaintenanceTimeline(maintenanceReports),
      monthlyTrends: calculateMonthlyTrends(maintenanceReports),
      costByType: calculateCostByType(maintenanceReports),
      criticalVehicles: identifyCriticalVehicles(maintenanceReports)
    };
  }, [maintenanceReports]);

  // Memoize recommendations
  const recommendations = useMemo(() => {
    if (!maintenanceReports || !analyticsData) return [];
    return generateMaintenanceRecommendations(maintenanceReports, analyticsData.providerMetrics);
  }, [maintenanceReports, analyticsData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !maintenanceReports) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Erro ao carregar dados de manutenção.</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary metrics
  const totalWorkOrders = maintenanceReports.length;
  const completedOrders = maintenanceReports.filter(order => order.status === 'closed').length;
  const openOrders = maintenanceReports.filter(order => order.status === 'open').length;
  const totalCost = maintenanceReports.reduce((sum, order) => sum + order.cost, 0);
  const avgCostPerOrder = totalWorkOrders > 0 ? totalCost / totalWorkOrders : 0;

  // Cost by provider
  const costByProvider = maintenanceReports.reduce((acc, order) => {
    const provider = order.provider_name || 'Sem Fornecedor';
    acc[provider] = (acc[provider] || 0) + order.cost;
    return acc;
  }, {} as Record<string, number>);

  const providerData = Object.entries(costByProvider)
    .map(([name, cost]) => ({ name, cost }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 10);

  // Monthly trend
  const monthlyData = maintenanceReports.reduce((acc, order) => {
    if (order.opened_at) {
      try {
        const date = parseISO(order.opened_at);
        if (isValid(date)) {
          const monthKey = format(date, 'yyyy-MM');
          acc[monthKey] = (acc[monthKey] || 0) + order.cost;
        }
      } catch (e) {
        // Ignore invalid dates
      }
    }
    return acc;
  }, {} as Record<string, number>);

  const trendData = Object.entries(monthlyData)
    .map(([month, cost]) => ({
      month: format(parseISO(month + '-01'), 'MMM/yy', { locale: ptBR }),
      cost
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatTableDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return '-';
      return format(date, "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const formatDateWithUrgency = (dateString: string | null | undefined, isCalculated: boolean = false) => {
    if (!dateString) return <span className="text-muted-foreground">-</span>;
    
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return <span className="text-muted-foreground">-</span>;
      
      const isPast = date < new Date();
      const daysUntil = differenceInDays(date, new Date());
      const isWithin7Days = daysUntil <= 7 && daysUntil >= 0;
      
      return (
        <div className="flex items-center gap-2">
          <Calendar className={`h-4 w-4 ${isPast ? 'text-red-500' : isWithin7Days ? 'text-orange-500' : 'text-muted-foreground'}`} />
          <span className={isPast ? 'text-red-600 font-semibold' : isWithin7Days ? 'text-orange-600 font-medium' : ''}>
            {format(date, "dd/MM/yyyy", { locale: ptBR })}
          </span>
          {isPast && <Badge variant="destructive" className="text-xs">VENCIDO</Badge>}
          {isCalculated && (
            <UITooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3 text-blue-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Data calculada automaticamente</p>
              </TooltipContent>
            </UITooltip>
          )}
        </div>
      );
    } catch {
      return <span className="text-muted-foreground">-</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string; icon: any }> = {
      open: { 
        label: 'Em Andamento', 
        className: 'bg-blue-500 text-white',
        icon: Clock 
      },
      closed: { 
        label: 'Concluído', 
        className: 'bg-green-500 text-white',
        icon: CheckCircle 
      },
      pending: { 
        label: 'Pendente', 
        className: 'bg-orange-500 text-white',
        icon: AlertCircle 
      },
      in_progress: { 
        label: 'Em Andamento', 
        className: 'bg-blue-500 text-white',
        icon: Clock 
      },
      cancelled: { 
        label: 'Cancelado', 
        className: 'bg-red-500 text-white',
        icon: XCircle 
      }
    };

    const statusInfo = statusMap[status.toLowerCase()] || {
      label: status,
      className: 'bg-gray-500 text-white',
      icon: HelpCircle
    };

    const Icon = statusInfo.icon;
    
    return (
      <Badge className={`${statusInfo.className} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'closed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'open':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const pendingCount = pendingReports?.length || 0;
  const criticalPending = pendingReports?.filter(p => {
    const isCriticalKm = p.km_until_maintenance !== null && p.km_until_maintenance < 500;
    const isCriticalDate = p.days_until_maintenance !== null && p.days_until_maintenance <= 7 && p.days_until_maintenance >= 0;
    const isOverdue = p.days_until_maintenance !== null && p.days_until_maintenance < 0;
    const isOverdueKm = p.km_until_maintenance !== null && p.km_until_maintenance < 0;
    return isCriticalKm || isCriticalDate || isOverdue || isOverdueKm;
  }).length || 0;

  const overdueCount = pendingReports?.filter(p => 
    (p.days_until_maintenance !== null && p.days_until_maintenance < 0) ||
    (p.km_until_maintenance !== null && p.km_until_maintenance < 0)
  ).length || 0;

  const handleExport = async (format: 'excel' | 'pdf') => {
    const exportAnalytics = analyticsData ? {
      providerPerformance: analyticsData.providerMetrics,
      monthlyTrend: analyticsData.monthlyTrends,
      costByType: analyticsData.costByType,
      criticalVehicles: analyticsData.criticalVehicles,
      recommendations: recommendations,
      timelineEvents: analyticsData.timeline,
      pendingMaintenance: {
        plans: (pendingReports || []).map(p => ({
          vehiclePlate: p.vehicle_plate,
          planName: p.plan_name,
          nextExecutionDate: p.next_execution_date,
          daysUntilMaintenance: p.days_until_maintenance,
          kmUntilMaintenance: p.km_until_maintenance,
          isOverdue: (p.days_until_maintenance !== null && p.days_until_maintenance < 0) || (p.km_until_maintenance !== null && p.km_until_maintenance < 0),
          isCritical: (p.days_until_maintenance !== null && p.days_until_maintenance >= 0 && p.days_until_maintenance <= 7) || (p.km_until_maintenance !== null && p.km_until_maintenance < 500 && p.km_until_maintenance >= 0)
        })),
        totalPending: pendingReports?.length || 0,
        overdue: pendingReports?.filter(p => 
          (p.days_until_maintenance !== null && p.days_until_maintenance < 0) ||
          (p.km_until_maintenance !== null && p.km_until_maintenance < 0)
        ).length || 0,
        critical: pendingReports?.filter(p => 
          (p.days_until_maintenance !== null && p.days_until_maintenance >= 0 && p.days_until_maintenance <= 7) ||
          (p.km_until_maintenance !== null && p.km_until_maintenance < 500 && p.km_until_maintenance >= 0)
        ).length || 0,
        estimatedCost: 0
      }
    } : undefined;

    await exportReport({
      data: maintenanceReports || [],
      type: 'maintenance',
      format: format,
      company: activeCompany,
      filename: `relatorio-manutencoes-${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`,
      maintenanceAnalytics: exportAnalytics,
    });
  };

  return (
    <div className="space-y-6">
      <ReportHeader 
        title="Relatórios de Manutenção"
        subtitle="Análise completa de ordens de serviço, custos e manutenções pendentes"
        onExportPDF={() => handleExport('pdf')}
        onExportExcel={() => handleExport('excel')}
        onPrint={() => window.print()}
      />

      {/* Summary Cards */}
      <KPIGrid columns={4}>
        <KPICard
          title="Total de OS"
          value={totalWorkOrders.toString()}
          icon={Wrench}
          description="Ordens de serviço registradas"
        />
        <KPICard
          title="OS Concluídas"
          value={completedOrders.toString()}
          icon={CheckCircle}
          description={`${totalWorkOrders > 0 ? ((completedOrders / totalWorkOrders) * 100).toFixed(1) : '0'}% do total`}
        />
        <KPICard
          title="OS Abertas"
          value={openOrders.toString()}
          icon={Clock}
          description="Em andamento"
        />
        <KPICard
          title="Custo Total"
          value={formatCurrency(totalCost)}
          icon={Wrench}
          description="Investimento em manutenção"
        />
        <KPICard
          title="Manutenções Pendentes"
          value={pendingCount.toString()}
          icon={AlertCircle}
          description={criticalPending > 0 ? `${criticalPending} críticas` : 'Nenhuma crítica'}
        />
      </KPIGrid>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Custos por Fornecedor</CardTitle>
            <CardDescription>
              Top 10 fornecedores por valor de serviços
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={providerData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: 'hsl(var(--foreground))' }}
                />
                <YAxis tick={{ fill: 'hsl(var(--foreground))' }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value as number)}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="cost" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Evolução Mensal de Custos</CardTitle>
            <CardDescription>
              Tendência de gastos com manutenção ao longo do tempo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fill: 'hsl(var(--foreground))' }} />
                <YAxis tick={{ fill: 'hsl(var(--foreground))' }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value as number)}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="completed" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="completed">
            <CheckCircle className="h-4 w-4 mr-2" />
            Manutenções Realizadas
          </TabsTrigger>
          <TabsTrigger value="pending">
            <Calendar className="h-4 w-4 mr-2" />
            Manutenções Pendentes
          </TabsTrigger>
        </TabsList>

        {/* COMPLETED TAB */}
        <TabsContent value="completed" className="space-y-6">
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="analysis">Análise</TabsTrigger>
              <TabsTrigger value="recommendations">Recomendações</TabsTrigger>
            </TabsList>

            {/* OVERVIEW SUB-TAB */}
            <TabsContent value="overview" className="space-y-6">
              {/* Detailed KPIs */}
              <KPIGrid columns={4}>
                <KPICard
                  title="Custo Médio por OS"
                  value={formatCurrency(avgCostPerOrder)}
                  icon={Wrench}
                  description="Valor médio por ordem"
                />
                <KPICard
                  title="Taxa de Conclusão"
                  value={`${totalWorkOrders > 0 ? ((completedOrders / totalWorkOrders) * 100).toFixed(1) : '0'}%`}
                  icon={CheckCircle}
                  description="Ordens concluídas"
                />
                {analyticsData && (
                  <>
                    <KPICard
                      title="Fornecedores Ativos"
                      value={analyticsData.providerMetrics.length.toString()}
                      icon={PieChartIcon}
                      description="Prestadores de serviço"
                    />
                    <KPICard
                      title="Veículos Críticos"
                      value={analyticsData.criticalVehicles.length.toString()}
                      icon={AlertCircle}
                      description="Requerem atenção"
                    />
                  </>
                )}
              </KPIGrid>

              {/* Distribution Chart */}
              {analyticsData && (
                <Card>
                  <CardHeader>
                    <CardTitle>Distribuição por Tipo de Manutenção</CardTitle>
                    <CardDescription>
                      Preventiva vs Corretiva
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Preventiva', value: analyticsData.costByType.preventive.cost, color: 'hsl(var(--success))' },
                            { name: 'Corretiva', value: analyticsData.costByType.corrective.cost, color: 'hsl(var(--destructive))' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          dataKey="value"
                        >
                          <Cell fill="hsl(var(--success))" />
                          <Cell fill="hsl(var(--destructive))" />
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Maintenance Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Detalhamento de Ordens de Serviço</CardTitle>
                  <CardDescription>
                    Histórico completo de manutenções realizadas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">N° OS</TableHead>
                        <TableHead className="w-[120px]">Veículo</TableHead>
                        <TableHead className="w-[150px] hidden md:table-cell">Fornecedor</TableHead>
                        <TableHead className="w-[120px]">Tipo</TableHead>
                        <TableHead className="text-right w-[120px]">Custo</TableHead>
                        <TableHead className="w-[110px]">Abertura</TableHead>
                        <TableHead className="w-[110px] hidden lg:table-cell">Fechamento</TableHead>
                        <TableHead className="text-center w-[120px]">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {maintenanceReports.map((order) => (
                        <TableRow key={order.work_order_id}>
                          <TableCell className="font-medium">
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2 cursor-help">
                                    {getStatusIcon(order.status)}
                                    <Badge variant="outline" className="font-mono">
                                      {formatWorkOrderNumber(order.order_number)}
                                    </Badge>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Ordem de Serviço #{order.order_number}</p>
                                  <p className="text-xs text-muted-foreground">ID: {order.work_order_id.slice(0, 8)}...</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell className="font-medium whitespace-nowrap">{order.vehicle_plate}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {order.provider_name !== 'Sem Fornecedor' ? (
                              order.provider_name
                            ) : (
                              <span className="text-muted-foreground italic">Não informado</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {order.maintenance_type || <span className="text-muted-foreground italic">Não especificado</span>}
                          </TableCell>
                          <TableCell className="text-right font-medium whitespace-nowrap">
                            {formatCurrency(order.cost)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{formatTableDate(order.opened_at)}</TableCell>
                          <TableCell className="hidden lg:table-cell whitespace-nowrap">{formatTableDate(order.closed_at)}</TableCell>
                          <TableCell className="text-center">
                            {getStatusBadge(order.status)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PERFORMANCE SUB-TAB */}
            <TabsContent value="performance">
              {analyticsData && (
                <ProviderPerformanceComparison providers={analyticsData.providerMetrics} />
              )}
            </TabsContent>

            {/* TIMELINE SUB-TAB */}
            <TabsContent value="timeline">
              {analyticsData && (
                <MaintenanceTimeline events={analyticsData.timeline} />
              )}
            </TabsContent>

            {/* ANALYSIS SUB-TAB */}
            <TabsContent value="analysis">
              {analyticsData && (
                <MaintenanceCostAnalysis 
                  monthlyTrends={analyticsData.monthlyTrends}
                  costByType={analyticsData.costByType}
                />
              )}
            </TabsContent>

            {/* RECOMMENDATIONS SUB-TAB */}
            <TabsContent value="recommendations">
              <MaintenanceRecommendations recommendations={recommendations} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* PENDING TAB */}
        <TabsContent value="pending" className="space-y-6">
          {/* Pending KPIs */}
          <KPIGrid columns={4}>
            <KPICard
              title="Total Pendente"
              value={pendingCount.toString()}
              icon={Calendar}
              description="Manutenções programadas"
            />
            <KPICard
              title="Críticas"
              value={criticalPending.toString()}
              icon={AlertCircle}
              description="< 7 dias ou < 500km"
            />
            <KPICard
              title="Vencidas"
              value={overdueCount.toString()}
              icon={XCircle}
              description="Requerem ação imediata"
            />
          </KPIGrid>

          {/* Pending Table */}
          <Card>
            <CardHeader>
              <CardTitle>Planos de Manutenção Pendentes</CardTitle>
              <CardDescription>
                Manutenções programadas que precisam ser executadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isPendingLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Carregando manutenções pendentes...
                </div>
              ) : pendingReports && pendingReports.length > 0 ? (
                <TooltipProvider>
                    <Table className="w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Plano</TableHead>
                        <TableHead className="w-[140px]">Veículo</TableHead>
                        <TableHead className="w-[120px]">Tipo</TableHead>
                        <TableHead className="w-[150px]">Próxima Data</TableHead>
                        <TableHead className="w-[110px]">Dias Restantes</TableHead>
                        <TableHead className="w-[110px] hidden md:table-cell">Próximo KM</TableHead>
                        <TableHead className="w-[120px] hidden md:table-cell">KM Restantes</TableHead>
                        <TableHead className="text-center w-[120px]">Urgência</TableHead>
                        <TableHead className="text-center w-[120px]">Status do Plano</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReports.map((plan) => {
                        const isCriticalKm = plan.km_until_maintenance !== null && plan.km_until_maintenance < 500;
                        const isOverdueKm = plan.km_until_maintenance !== null && plan.km_until_maintenance < 0;
                        const isCriticalDate = plan.days_until_maintenance !== null && plan.days_until_maintenance <= 7 && plan.days_until_maintenance >= 0;
                        const isOverdueDate = plan.days_until_maintenance !== null && plan.days_until_maintenance < 0;
                        const isCritical = isCriticalKm || isCriticalDate || isOverdueKm || isOverdueDate;

                        return (
                          <TableRow key={plan.plan_id} className={isCritical ? "bg-orange-50 dark:bg-orange-950/20" : ""}>
                            <TableCell className="font-medium">
                              <div className="flex flex-col">
                                <span>{plan.plan_name}</span>
                                <span className="text-xs text-muted-foreground">{plan.plan_id.slice(0, 8)}...</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium whitespace-nowrap">{plan.vehicle_plate}</span>
                                <span className="text-xs text-muted-foreground">{plan.vehicle_model}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {plan.maintenance_type || <span className="text-muted-foreground italic">Não especificado</span>}
                            </TableCell>
                            <TableCell>
                              {formatDateWithUrgency(plan.next_execution_date || null, plan.is_date_calculated)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {plan.days_until_maintenance !== null ? (
                                <span className={
                                  plan.days_until_maintenance < 0 ? "font-semibold text-red-600" :
                                  plan.days_until_maintenance <= 7 ? "font-semibold text-orange-600" :
                                  "text-foreground"
                                }>
                                  {plan.days_until_maintenance < 0 
                                    ? `${Math.abs(plan.days_until_maintenance)} dias VENCIDO`
                                    : `${plan.days_until_maintenance} dias`
                                  }
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-right whitespace-nowrap">
                              {plan.next_execution_km ? (
                                `${plan.next_execution_km.toLocaleString('pt-BR')} km`
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-right whitespace-nowrap">
                              {plan.km_until_maintenance !== null ? (
                                <span className={
                                  plan.km_until_maintenance < 0 ? "font-semibold text-red-600" :
                                  plan.km_until_maintenance < 500 ? "font-semibold text-orange-600" :
                                  "text-foreground"
                                }>
                                  {plan.km_until_maintenance < 0
                                    ? `${Math.abs(plan.km_until_maintenance).toLocaleString('pt-BR')} km EXCEDIDO`
                                    : `${plan.km_until_maintenance.toLocaleString('pt-BR')} km`
                                  }
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isOverdueDate || isOverdueKm ? (
                                <Badge variant="destructive" className="flex items-center gap-1 w-fit mx-auto">
                                  <XCircle className="h-3 w-3" />
                                  VENCIDO
                                </Badge>
                              ) : isCritical ? (
                                <Badge variant="destructive">URGENTE</Badge>
                              ) : (
                                <Badge variant="secondary">Programada</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {(() => {
                                const status = plan.calculated_status || plan.status || 'active';
                                
                                if (status === 'in_progress') {
                                  return <Badge variant="destructive">Em Andamento</Badge>;
                                } else if (status === 'inactive') {
                                  return <Badge variant="secondary">Inativo</Badge>;
                                } else if (status === 'completed') {
                                  return <Badge className="bg-green-600">Concluído</Badge>;
                                } else {
                                  return <Badge>Ativo</Badge>;
                                }
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhuma manutenção pendente no momento</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Active Alerts Section */}
      {analyticsData && analyticsData.criticalVehicles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Alertas de Manutenção Ativos</CardTitle>
            <CardDescription>
              Veículos que requerem atenção especial
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Veículo</TableHead>
                  <TableHead className="text-right w-[120px]">Manutenções</TableHead>
                  <TableHead className="text-right w-[140px]">Custo Total</TableHead>
                  <TableHead className="w-[120px]">Última Manutenção</TableHead>
                  <TableHead className="text-center w-[120px]">Prioridade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyticsData.criticalVehicles.map((vehicle) => (
                  <TableRow key={vehicle.vehicleId}>
                    <TableCell className="font-medium whitespace-nowrap">{vehicle.vehiclePlate}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{vehicle.maintenanceCount}</TableCell>
                    <TableCell className="text-right font-semibold text-destructive whitespace-nowrap">
                      {formatCurrency(vehicle.totalCost)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatTableDate(vehicle.lastMaintenance)}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="destructive">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        ALTA
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
